import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  ShieldCheck,
  History,
  Monitor,
  Smartphone,
  Tablet,
  LogOut,
  Clock,
  Globe,
  AlertCircle,
  Activity,
  User,
  MapPin,
  ChevronRight,
  Zap
} from "lucide-react";
import { Breadcrumb } from "../Breadcrumb";
import API from "../api/api01";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { cn } from "../ui/utils";
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, 
  AlertDialogTitle 
} from "../ui/alert-dialog";

interface AuditLog {
  id: string;
  action: string;
  table_name: string;
  record_id: string;
  timestamp: string;
  ip_address: string;
  old_data?: any;
  new_data?: any;
  changed_fields?: string[];
}


interface SecuritySettings {
  session_timeout_minutes: number;
  ip_whitelist: string;
  max_login_attempts: number;
  lockout_duration_minutes: number;
  is_active: boolean;
}

interface LoginHistory {
  id: string;
  ip_address: string;
  user_id: string;
  user_name: string;
  user_email: string;
  status: string;
  failure_reason: string;
  location: string;
  created_at: string;
}

interface BlockedIP {
  id: string;
  ip_address: string;
  reason: string;
  is_active: boolean;
  created_at: string;
  threat_score?: number;
  is_global?: boolean;
}

interface ThreatAnomaly {
  ip: string;
  identities_targeted: number;
  attempts: number;
  risk_level: 'High' | 'Medium';
}

interface GlobalThreat {
  ip: string;
  score: number;
  reason: string;
  created_at: string;
}


export function SecurityPortal({ initialTab }: { initialTab?: string }) {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loginHistory, setLoginHistory] = useState<LoginHistory[]>([]);
  const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([]);
  const [activeTab, setActiveTab] = useState(initialTab || "ip-tracking");

  const [settings, setSettings] = useState<SecuritySettings>({
    session_timeout_minutes: 60,
    ip_whitelist: "",
    max_login_attempts: 5,
    lockout_duration_minutes: 15,
    is_active: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'stream' | 'intelligence'>('stream');
  const [searchQuery, setSearchQuery] = useState("");
  const [showMultiUserOnly, setShowMultiUserOnly] = useState(false);
  const [threatData, setThreatData] = useState<{anomalies: ThreatAnomaly[], global_threats: GlobalThreat[]}>({
    anomalies: [],
    global_threats: []
  });
  const [ipToBlock, setIpToBlock] = useState<string | null>(null);



  const fetchData = async () => {
    try {
      setLoading(true);
      const [auditRes, settingsRes, loginRes, blockedRes, threatRes] = await Promise.all([
        API.get("/security/audit-trail"),
        API.get("/security/settings"),
        API.get("/security/login-history"),
        API.get("/security/ip-blocks"),
        API.get("/security/threat-intelligence")
      ]);
      setAuditLogs(auditRes.data);
      setSettings(settingsRes.data);
      setLoginHistory(loginRes.data);
      setBlockedIPs(blockedRes.data);
      setThreatData(threatRes.data);
    } catch (error) {
      console.error("Failed to fetch security data:", error);
      toast.error("Failed to sync security records");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);


  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await API.patch("/security/settings", settings);
      toast.success("Security settings updated");
    } catch (error) {
      toast.error("Failed to update security settings");
    } finally {
      setSaving(false);
    }
  };

  const handleBlockIP = async (ip: string) => {
    try {
      await API.post("/security/ip-blocks/", { ip_address: ip, reason: "Manual block from security portal" });
      toast.success(`IP ${ip} blocked successfully`);
      fetchData();
    } catch (error) {
      toast.error("Failed to block IP");
    }
  };

  const handleUnblockIP = async (id: string) => {
    try {
      await API.delete(`/security/ip-blocks/${id}/`);
      toast.success("IP unblocked successfully");
      setBlockedIPs(blockedIPs.filter(ip => ip.id !== id));
    } catch (error) {
      toast.error("Failed to unblock IP");
    }
  };



  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-[1400px] mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-primary" />
              Security & Audit Portal
            </h1>
            <Breadcrumb items={[{ label: "Security Center" }]} className="mt-2" />
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            className="rounded-xl border-white/10"
          >
            {loading ? "Syncing..." : "Refresh Status"}
          </Button>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList className="bg-white/5 p-1 rounded-xl border border-white/10 w-fit">
            <TabsTrigger value="ip-tracking" className="rounded-lg px-6 text-xs font-bold">IP Tracking & Blocking</TabsTrigger>
            <TabsTrigger value="intelligence" className="rounded-lg px-6 text-xs font-bold flex gap-2">
              <Zap className="h-3 w-3 text-amber-500" />
              Threat Intelligence
            </TabsTrigger>
            <TabsTrigger value="audit" className="rounded-lg px-6 text-xs font-bold">System Audit</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg px-6 text-xs font-bold">Security Policy</TabsTrigger>
          </TabsList>



          <TabsContent value="audit">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  System Activity Log
                </CardTitle>
                <CardDescription>Real-time audit of all administrative actions and security events.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative space-y-0 before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-white/5 before:to-transparent">
                  {auditLogs.map((log, index) => (
                    <div key={log.id} className="border-b border-white/5 last:border-0">
                      <div
                        className="relative flex items-center justify-between p-4 group cursor-pointer hover:bg-white/[0.02] transition-colors"
                        onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      >
                        <div className="flex items-center gap-4">
                          <div className={cn(
                            "relative h-10 w-10 flex items-center justify-center rounded-full border z-10 transition-colors",
                            log.action === "FAILED_LOGIN" ? "bg-red-500/10 border-red-500/20 text-red-500" :
                              log.action === "LOGIN" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500" :
                                "bg-white/5 border-white/10 text-muted-foreground group-hover:bg-primary/20 group-hover:text-primary"
                          )}>
                            {log.action === "FAILED_LOGIN" ? <AlertCircle className="h-5 w-5" /> : <History className="h-5 w-5" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                "text-sm font-bold uppercase tracking-wider",
                                log.action === "FAILED_LOGIN" ? "text-red-400" :
                                  log.action === "LOGIN" ? "text-emerald-400" : ""
                              )}>{log.action}</span>
                              <Badge variant="outline" className="text-[10px] py-0">{log.table_name}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {log.action === "FAILED_LOGIN" ? `Failed attempt from ${log.ip_address}` :
                                log.action === "UPDATE" ? `Modified ${log.changed_fields?.length || 0} fields` :
                                  `System action on ${log.table_name}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-xs font-bold text-foreground">{format(new Date(log.timestamp), "HH:mm:ss")}</p>
                            <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">{format(new Date(log.timestamp), "MMM dd, yyyy")}</p>
                          </div>
                          <ChevronRight className={cn(
                            "h-4 w-4 text-muted-foreground transition-transform",
                            expandedLog === log.id ? "rotate-90" : ""
                          )} />
                        </div>
                      </div>

                      {expandedLog === log.id && (log.old_data || log.new_data) && (
                        <div className="px-4 pb-4 ml-14 space-y-3">
                          <div className="grid grid-cols-1 gap-2">
                            {log.changed_fields?.map(field => (
                              <div key={field} className="flex flex-col p-2 rounded-lg bg-white/5 border border-white/5">
                                <span className="text-[10px] font-bold text-primary uppercase tracking-tighter mb-1">{field.replace('_', ' ')}</span>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <span className="text-[9px] text-muted-foreground uppercase">From</span>
                                    <p className="text-xs text-red-300 font-mono truncate">{String(log.old_data?.[field] ?? 'None')}</p>
                                  </div>
                                  <div>
                                    <span className="text-[9px] text-muted-foreground uppercase">To</span>
                                    <p className="text-xs text-emerald-300 font-mono truncate">{String(log.new_data?.[field] ?? 'None')}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {log.action === "CREATE" && log.new_data && (
                              <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                                <span className="text-[10px] font-bold text-emerald-500 uppercase mb-2 block">Initial Data</span>
                                <pre className="text-[10px] text-emerald-200/70 font-mono whitespace-pre-wrap">
                                  {JSON.stringify(log.new_data, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-[9px] text-muted-foreground pt-2 border-t border-white/5">
                            <span className="flex items-center gap-1"><MapPin className="h-2.5 w-2.5" /> {log.ip_address}</span>
                            <span className="flex items-center gap-1"><User className="h-2.5 w-2.5" /> {log.record_id}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}


                  {auditLogs.length === 0 && (
                    <div className="py-20 text-center space-y-3">
                      <History className="h-12 w-12 text-muted-foreground/20 mx-auto" />
                      <p className="text-muted-foreground/60 font-medium">No system activities recorded yet.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ip-tracking" className="space-y-6">
            {/* Stats Tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="glass-card p-4 flex items-center gap-4 border-white/5">
                <div className="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400">
                  <Activity className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Total Attempts</p>
                  <p className="text-xl font-bold">{loginHistory.length}</p>
                </div>
              </Card>
              <Card className="glass-card p-4 flex items-center gap-4 border-white/5">
                <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Success Rate</p>
                  <p className="text-xl font-bold">
                    {loginHistory.length > 0 
                      ? Math.round((loginHistory.filter(l => l.status === 'success').length / loginHistory.length) * 100) 
                      : 0}%
                  </p>
                </div>
              </Card>
              <Card className="glass-card p-4 flex items-center gap-4 border-red-500/10">
                <div className="h-10 w-10 rounded-xl bg-red-500/10 flex items-center justify-center text-red-400">
                  <AlertCircle className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Blocked IPs</p>
                  <p className="text-xl font-bold">{blockedIPs.length}</p>
                </div>
              </Card>
              <Card className="glass-card p-4 flex items-center gap-4 border-white/5">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Last Incident</p>
                  <p className="text-xs font-bold">
                    {loginHistory.find(l => l.status === 'failed') 
                      ? format(new Date(loginHistory.find(l => l.status === 'failed')!.created_at), "HH:mm:ss")
                      : "No threats"}
                  </p>
                </div>
              </Card>
            </div>
            
            {/* View Controls & Search */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-900/40 p-4 rounded-2xl border border-white/5">
              <div className="flex p-1 bg-slate-950/50 rounded-xl border border-white/10">
                <button
                  onClick={() => setViewMode('stream')}
                  className={cn(
                    "px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all",
                    viewMode === 'stream' ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20" : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  Live Stream
                </button>
                <button
                  onClick={() => setViewMode('intelligence')}
                  className={cn(
                    "px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all",
                    viewMode === 'intelligence' ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20" : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  IP Intelligence
                </button>
              </div>

              <div className="flex items-center gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search IP or Username..."
                    className="w-full bg-slate-950/50 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-[11px] focus:outline-none focus:border-primary/50 text-white"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                {viewMode === 'intelligence' && (
                  <button
                    onClick={() => setShowMultiUserOnly(!showMultiUserOnly)}
                    className={cn(
                      "px-3 py-2 rounded-xl text-[9px] font-bold uppercase border transition-all",
                      showMultiUserOnly ? "bg-amber-500/10 text-amber-500 border-amber-500/20" : "bg-white/5 text-slate-500 border-white/10"
                    )}
                  >
                    Multi-User Only
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Login History - Larger span */}
              <Card className="glass-card lg:col-span-2">
                <CardHeader className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {viewMode === 'stream' ? <History className="h-4 w-4 text-primary" /> : <Globe className="h-4 w-4 text-primary" />}
                        {viewMode === 'stream' ? 'Live Access Log' : 'IP Intelligence Hub'}
                      </CardTitle>
                      <CardDescription className="text-[10px]">
                        {viewMode === 'stream' 
                          ? 'Real-time monitoring of all authentication attempts.' 
                          : 'Advanced grouping and threat analysis by IP address.'}
                      </CardDescription>
                    </div>
                    <Badge variant="outline" className="text-[9px] uppercase font-bold bg-white/5">Auto-Refresh Active</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {viewMode === 'stream' ? (
                    <div className="overflow-x-auto max-h-[500px] scrollbar-hide">
                      <table className="w-full text-sm text-left border-collapse">
                        <thead className="text-[10px] uppercase bg-white/[0.03] text-muted-foreground font-bold sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-2 border-b border-white/5">Endpoint / IP</th>
                            <th className="px-4 py-2 border-b border-white/5">Identity</th>
                            <th className="px-4 py-2 border-b border-white/5">Status</th>
                            <th className="px-4 py-2 border-b border-white/5 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {loginHistory
                            .filter(log => 
                              log.ip_address.includes(searchQuery) || 
                              log.user_name.toLowerCase().includes(searchQuery.toLowerCase())
                            )
                            .map((log) => (
                            <tr key={log.id} className="hover:bg-white/[0.01] transition-colors group">
                              <td className="px-4 py-2">
                                <div className="flex flex-col">
                                  <span className="font-mono font-bold text-[11px] group-hover:text-primary transition-colors">{log.ip_address}</span>
                                  <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground">
                                    <MapPin className="h-2 w-2 text-primary/60" />
                                    <span className="truncate max-w-[180px]">{log.location || "Unknown Location"}</span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex flex-col">
                                  <span className="text-[11px] font-bold">{log.user_name}</span>
                                  <span className="text-[9px] text-muted-foreground truncate max-w-[150px]">
                                    {log.user_email !== 'N/A' ? log.user_email : 'System Guest'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex items-center gap-2">
                                  {log.status === 'success' ? (
                                    <Badge className="h-5 bg-emerald-500/10 text-emerald-500 border-emerald-500/10 text-[9px] uppercase font-bold">Authorized</Badge>
                                  ) : (
                                    <Badge variant="destructive" className="h-5 bg-red-500/10 text-red-500 border-red-500/10 text-[9px] uppercase font-bold w-fit">Blocked</Badge>
                                  )}
                                  <span className="text-[9px] text-slate-500 font-bold whitespace-nowrap">
                                    {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-2 text-right">
                                {blockedIPs.some(b => b.ip_address === log.ip_address) ? (
                                  <Badge variant="outline" className="h-6 text-[8px] uppercase font-bold border-red-500/20 text-red-400">Blacklisted</Badge>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-[9px] font-bold text-muted-foreground hover:text-red-500 hover:bg-red-500/5 px-2"
                                    onClick={() => setIpToBlock(log.ip_address)}
                                  >
                                    Restrict
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    /* Intelligence View - Tiles */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto pr-2 scrollbar-hide">
                      {Object.values(loginHistory.reduce((acc, log) => {
                        if (!acc[log.ip_address]) {
                          acc[log.ip_address] = {
                            ip: log.ip_address,
                            users: new Set(),
                            attempts: 0,
                            last_seen: log.created_at,
                            location: log.location,
                            failed: 0
                          };
                        }
                        acc[log.ip_address].users.add(log.user_name);
                        acc[log.ip_address].attempts += 1;
                        if (log.status !== 'success') acc[log.ip_address].failed += 1;
                        if (new Date(log.created_at) > new Date(acc[log.ip_address].last_seen)) {
                          acc[log.ip_address].last_seen = log.created_at;
                        }
                        return acc;
                      }, {} as Record<string, any>))
                      .filter((ip: any) => {
                        const matchesSearch = ip.ip.includes(searchQuery) || Array.from(ip.users as Set<string>).some(u => u.toLowerCase().includes(searchQuery.toLowerCase()));
                        const matchesMulti = !showMultiUserOnly || (ip.users as Set<string>).size > 1;
                        return matchesSearch && matchesMulti;
                      })
                      .map((ip: any) => (
                        <div key={ip.ip} className="p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-primary/30 transition-all space-y-4">
                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <h3 className="text-sm font-mono font-black text-white">{ip.ip}</h3>
                              <p className="text-[9px] font-bold text-slate-500 uppercase flex items-center gap-1">
                                <MapPin className="h-2 w-2" /> {ip.location || "Unknown Location"}
                              </p>
                            </div>
                            {ip.users.size > 1 && (
                              <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[8px] font-black uppercase">Shared IP</Badge>
                            )}
                          </div>

                          <div className="space-y-2">
                            <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Associated Identities ({ip.users.size})</p>
                            <div className="flex flex-wrap gap-1.5">
                              {Array.from(ip.users as Set<string>).map(user => (
                                <Badge key={user} variant="outline" className="text-[9px] font-bold py-0.5 bg-white/5 border-white/10">
                                  {user}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          <div className="pt-3 border-t border-white/5 flex justify-between items-end">
                            <div className="space-y-1">
                              <p className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">Last Seen</p>
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                <p className="text-[10px] font-bold text-slate-300">{formatDistanceToNow(new Date(ip.last_seen), { addSuffix: true })}</p>
                              </div>
                              <p className="text-[8px] text-slate-600 font-bold uppercase">{format(new Date(ip.last_seen), "MMM dd, HH:mm:ss")}</p>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <div className="flex gap-2 text-[9px] font-bold">
                                <span className="text-emerald-500">{ip.attempts - ip.failed} OK</span>
                                <span className="text-red-500">{ip.failed} ERR</span>
                              </div>
                              {!blockedIPs.some(b => b.ip_address === ip.ip) ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-3 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 text-[8px] font-black uppercase"
                                  onClick={() => setIpToBlock(ip.ip)}
                                >
                                  Restrict IP
                                </Button>
                              ) : (
                                <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[8px] font-black uppercase">Restricted</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Blocked IPs - Smaller span */}
              <Card className="glass-card border-red-500/5">
                <CardHeader className="py-4">
                  <CardTitle className="text-base flex items-center gap-2 text-red-400">
                    <ShieldCheck className="h-4 w-4" />
                    Security Firewall
                  </CardTitle>
                  <CardDescription className="text-[10px]">Managing restricted access points.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-[10px] uppercase bg-white/[0.03] text-muted-foreground font-bold">
                        <tr>
                          <th className="px-4 py-2 border-b border-white/5">Restricted IP</th>
                          <th className="px-4 py-2 border-b border-white/5 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {blockedIPs.map((blocked) => (
                          <tr key={blocked.id} className="hover:bg-white/[0.01] transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex flex-col">
                                <span className="font-mono font-bold text-red-400/80 text-[11px]">{blocked.ip_address}</span>
                                <span className="text-[8px] text-muted-foreground uppercase">{format(new Date(blocked.created_at), "MMM dd, yyyy")}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-[9px] font-bold text-emerald-400 hover:bg-emerald-500/5"
                                onClick={() => handleUnblockIP(blocked.id)}
                              >
                                Restore
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {blockedIPs.length === 0 && (
                          <tr>
                            <td colSpan={2} className="px-4 py-12 text-center text-[10px] text-muted-foreground italic">
                              Firewall is clear. No active blocks.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="intelligence" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Anomalous Activity */}
              <Card className="glass-card border-amber-500/10">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    Account Harvesting Alerts
                  </CardTitle>
                  <CardDescription className="text-[10px]">IPs currently attempting to access multiple user identities.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="space-y-0">
                    {threatData.anomalies.map((anon, i) => (
                      <div key={anon.ip} className="p-4 border-b border-white/5 flex items-center justify-between hover:bg-white/5 transition-all">
                        <div className="flex items-center gap-3">
                          <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center font-black text-[10px]", 
                            anon.risk_level === 'High' ? "bg-red-500/10 text-red-500" : "bg-amber-500/10 text-amber-500")}>
                            {anon.risk_level === 'High' ? '!!!' : '!'}
                          </div>
                          <div>
                            <p className="text-xs font-mono font-black text-white">{anon.ip}</p>
                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                              Targeting {anon.identities_targeted} unique users
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 text-[9px] font-black uppercase text-red-400 hover:bg-red-500/10"
                          onClick={() => setIpToBlock(anon.ip)}
                        >
                          Auto-Block IP
                        </Button>
                      </div>
                    ))}
                    {threatData.anomalies.length === 0 && (
                      <div className="py-12 text-center text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em]">
                        No harvesting anomalies detected
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Global Threat Intelligence */}
              <Card className="glass-card border-primary/10">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    Global Platform Threats
                  </CardTitle>
                  <CardDescription className="text-[10px]">High-risk IPs blocked across the entire XeroAlpha platform.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="space-y-0">
                    {threatData.global_threats.map((threat) => (
                      <div key={threat.ip} className="p-4 border-b border-white/5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-black text-[10px]">
                            {threat.score}%
                          </div>
                          <div>
                            <p className="text-xs font-mono font-black text-white">{threat.ip}</p>
                            <p className="text-[9px] font-bold text-slate-500 uppercase truncate max-w-[200px]">
                              {threat.reason}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-primary/20 text-primary border-primary/30 text-[8px] font-black uppercase">Platform Ban</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <Card className="glass-card max-w-2xl">
              <CardHeader>
                <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <CardTitle>Protection Settings</CardTitle>
                <CardDescription>Configure advanced infrastructure security controls for your client.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="grid gap-2">
                    <label className="text-sm font-bold flex items-center gap-2">
                      <Globe className="h-4 w-4 text-blue-400" />
                      IP Whitelisting
                    </label>
                    <textarea
                      className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm focus:outline-none focus:border-primary/50 transition-colors min-h-[80px]"
                      placeholder="Enter comma-separated IPs (e.g. 192.168.1.1, 10.0.0.1)"
                      value={settings.ip_whitelist}
                      onChange={(e) => setSettings({ ...settings, ip_whitelist: e.target.value })}
                    />
                    <p className="text-[10px] text-muted-foreground">Leave empty to allow administrative access from all locations.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <label className="text-sm font-bold flex items-center gap-2">
                        <Clock className="h-4 w-4 text-emerald-400" />
                        Session Timeout
                      </label>
                      <input
                        type="number"
                        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm focus:outline-none focus:border-primary/50 transition-colors"
                        value={settings.session_timeout_minutes}
                        onChange={(e) => setSettings({ ...settings, session_timeout_minutes: parseInt(e.target.value) })}
                      />
                      <p className="text-[10px] text-muted-foreground">Minutes until auto-logout.</p>
                    </div>

                    <div className="grid gap-2">
                      <label className="text-sm font-bold flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-400" />
                        Failed Attempts
                      </label>
                      <input
                        type="number"
                        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm focus:outline-none focus:border-primary/50 transition-colors"
                        value={settings.max_login_attempts}
                        onChange={(e) => setSettings({ ...settings, max_login_attempts: parseInt(e.target.value) })}
                      />
                      <p className="text-[10px] text-muted-foreground">Max attempts before lockout.</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-2xl bg-primary/5 border border-primary/10">
                    <div className="flex gap-4 items-center">
                      <User className="h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-bold text-sm">Active Protection</h4>
                        <p className="text-xs text-muted-foreground">Enable all security enforcements for this client.</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      className="w-5 h-5 accent-primary"
                      checked={settings.is_active}
                      onChange={(e) => setSettings({ ...settings, is_active: e.target.checked })}
                    />
                  </div>
                </div>

                <Button
                  className="w-full rounded-xl gap-2 h-11"
                  onClick={handleSaveSettings}
                  disabled={saving}
                >
                  <ShieldCheck className="h-4 w-4" />
                  {saving ? "Deploying..." : "Update Security Policy"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>

      <AlertDialog open={!!ipToBlock} onOpenChange={(open) => !open && setIpToBlock(null)}>
        <AlertDialogContent className="glass-card border-white/10 sm:max-w-[450px] w-[95vw] fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Restrict Access for {ipToBlock}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will add {ipToBlock} to the system-wide blacklist. 
              The user at this location will be immediately disconnected and prevented from re-authenticating.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                if (ipToBlock) {
                  handleBlockIP(ipToBlock);
                  setIpToBlock(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Restrict IP
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
